import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  MessageCircle,
  Send,
  Loader2,
  Sparkles,
  Clock,
  Headphones,
} from "lucide-react";
import { toast } from "sonner";
import LegalShell from "@/components/digindia/LegalShell";

const inputCls =
  "w-full h-12 px-4 rounded-xl bg-foreground/[0.03] dark:bg-white/[0.04] border border-border focus:border-[#FF7A00] focus:ring-2 focus:ring-[#FF7A00]/30 outline-none transition-all text-sm md:text-base placeholder:text-muted-foreground/70";

const Field = ({ id, label, icon: Icon, children }) => (
  <label htmlFor={id} className="block">
    <div className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-2 flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-[#FF9933]" />
      {label}
    </div>
    {children}
  </label>
);

const InfoRow = ({ icon: Icon, label, value, accent, href, testid }) => {
  const Tag = href ? "a" : "div";
  return (
    <Tag
      {...(href ? { href, target: "_blank", rel: "noreferrer" } : {})}
      className="group flex items-start gap-4 p-4 rounded-xl border border-border hover:border-[#FF7A00]/40 hover:bg-foreground/[0.02] transition-all"
      data-testid={testid}
    >
      <div
        className="h-11 w-11 rounded-xl grid place-items-center shrink-0"
        style={{
          background: `${accent}1f`,
          border: `1px solid ${accent}40`,
        }}
      >
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
          {label}
        </div>
        <div className="font-semibold text-sm md:text-base mt-0.5 break-words">
          {value}
        </div>
      </div>
    </Tag>
  );
};

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    toast.dismiss();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    // eslint-disable-next-line no-console
    console.log("[DIGINDIA] Contact submitted:", form);
    setTimeout(() => {
      setSubmitting(false);
      toast.success(
        `Message received, ${form.name.split(" ")[0]}. We'll respond within 2 hours.`,
        { description: "Check your inbox — we sent a confirmation copy." }
      );
      setForm({ name: "", email: "", phone: "", message: "" });
    }, 800);
  };

  const whatsappLink = "https://wa.me/919999999999?text=" + encodeURIComponent("Hi DIGINDIA, I'd like to talk about my project.");

  return (
    <LegalShell
      kicker="Talk to us"
      title="Contact Us"
      accent="#FF7A00"
      icon={Headphones}
      intro="A real human, not a chatbot. Drop a line, ring our line, or ping our WhatsApp — we reply in under two hours during business hours."
      testid="contact-page"
    >
      <div className="grid lg:grid-cols-12 gap-8 md:gap-10">
        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          onSubmit={onSubmit}
          noValidate
          className="lg:col-span-7 dark:glass-dark glass-light rounded-3xl border border-border p-6 md:p-10 space-y-5"
          data-testid="contact-form"
        >
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#FF7A00] flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" />
              Send a message
            </div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight">
              Tell us what you&apos;re building.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field id="c-name" label="Name" icon={Sparkles}>
              <input
                id="c-name"
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder="Aarav Sharma"
                className={inputCls}
                required
                data-testid="contact-input-name"
              />
            </Field>
            <Field id="c-email" label="Email" icon={Mail}>
              <input
                id="c-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="aarav@yourbusiness.in"
                className={inputCls}
                required
                data-testid="contact-input-email"
              />
            </Field>
          </div>
          <Field id="c-phone" label="Phone" icon={Phone}>
            <input
              id="c-phone"
              type="tel"
              value={form.phone}
              onChange={update("phone")}
              placeholder="+91 98xxx xxxxx"
              className={inputCls}
              data-testid="contact-input-phone"
            />
          </Field>
          <Field id="c-message" label="Message" icon={MessageCircle}>
            <textarea
              id="c-message"
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us a bit about your goals…"
              rows={5}
              className={`${inputCls} h-auto py-3 resize-none`}
              required
              data-testid="contact-input-message"
            />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            className="group inline-flex items-center justify-center gap-3 w-full sm:w-auto h-12 px-7 rounded-full gradient-saffron text-white font-semibold text-sm shadow-[0_12px_40px_rgba(255,122,0,0.35)] hover:shadow-[0_18px_60px_rgba(255,122,0,0.5)] hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="contact-submit-button"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Send message
                <Send className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </button>
        </motion.form>

        {/* Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-5 space-y-4"
        >
          <div className="rounded-3xl dark:glass-dark glass-light border border-border p-6 md:p-7 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground">
              Reach us directly
            </div>
            <InfoRow
              icon={Mail}
              label="Email"
              value="care@digindiapaytech.com"
              accent="#FF7A00"
              href="mailto:care@digindiapaytech.com"
              testid="contact-info-email"
            />
            <InfoRow
              icon={Phone}
              label="Phone"
              value="+91 9743032112"
              accent="#FF9933"
              href="tel:+919999999999"
              testid="contact-info-phone"
            />
            <InfoRow
              icon={MapPin}
              label="Address"
              value="DIGINDIA PAYTECH, A-104, Cyber Hub, Sector 24, Gurugram, Haryana 122002, India"
              accent="#00C853"
              testid="contact-info-address"
            />
          </div>

          <a
            href={whatsappLink}
            target="_blank"
            rel="noreferrer"
            className="group relative flex items-center gap-4 p-5 rounded-2xl border border-[#00C853]/40 bg-[#00C853]/8 hover:bg-[#00C853]/15 transition-all overflow-hidden"
            data-testid="contact-whatsapp-button"
          >
            <span className="absolute -left-2 top-1/2 -translate-y-1/2 h-16 w-16 rounded-full bg-[#00C853]/30 blur-xl animate-pulse" />
            <span className="relative h-12 w-12 rounded-xl bg-[#00C853] grid place-items-center text-white animate-float">
              <MessageCircle className="h-5 w-5" />
            </span>
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#00C853]">
                WhatsApp · Chat now
              </div>
              <div className="font-semibold text-sm md:text-base">
                +91 9743032112
              </div>
            </div>
          </a>

          <div className="rounded-2xl dark:glass-dark glass-light border border-border p-5 flex items-center gap-3">
            <Clock className="h-5 w-5 text-[#FF9933]" />
            <div className="text-sm">
              <div className="font-semibold">Mon–Sat · 10 AM – 8 PM IST</div>
              <div className="text-muted-foreground text-xs">
                We reply within 2 hours during business hours.
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </LegalShell>
  );
}
